import type { ScheduleTaskType } from '@risksmart-app/domain/src/types/consts';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { StatusType } from 'src/components/form/controlled-select/ControlledSelect';
import { Controller } from 'src/components/form/field-controller/Controller';
import { FormField } from 'src/components/form/form/FormField';
import Multiselect from 'src/components/form/multi-select';

import type { ScheduleFormFieldData } from './scheduleFormSchema';
import type { ScheduleTarget } from './scheduleTargets';
import {
  pickSelectedOptions,
  replaceTargetsOfTaskType,
} from './scheduleTargets';

type Props = {
  taskType: ScheduleTaskType;
  label: string;
  options: ScheduleTarget[];
  statusType: StatusType;
};

/**
 * One picker per selected task type, all writing the single flat `Targets`
 * array. Each one owns only its own type's slice, so it must merge rather than
 * replace — a plain controlled multiselect on `Targets` would drop the other
 * types' selections on every change.
 */
export const TargetField: FC<Props> = ({
  taskType,
  label,
  options,
  statusType,
}) => {
  const { t } = useTranslation(['common']);
  const { control, getValues, setValue } =
    useFormContext<ScheduleFormFieldData>();
  const { error } = control.getFieldState('Targets');

  return (
    <Controller
      name={'Targets'}
      control={control}
      render={({ field: { ref, onChange, onBlur, value } }) => {
        const selected = value.filter((target) => target.Type === taskType);

        return (
          <FormField
            label={label}
            // The schema raises one issue on `Targets` for the whole set, so it
            // is shown against the pickers that are actually empty.
            errorText={selected.length === 0 ? error?.message : undefined}
            stretch={true}
            testId={`schedule-targets-${taskType}`}
          >
            <Multiselect
              ref={ref}
              options={options}
              // `Multiselect` passes this straight through rather than deciding
              // for itself (see the comment in ../../../components/form/select),
              // and the schedulable-targets read is unbounded, so a register-scale
              // org renders one Option component per row without it.
              virtualScroll={options.length >= 500}
              statusType={statusType}
              selectedOptions={selected}
              onBlur={onBlur}
              onChange={(e) => {
                const next = replaceTargetsOfTaskType(
                  getValues(),
                  taskType,
                  pickSelectedOptions(options, e.detail.selectedOptions)
                );
                setValue('AssigneesByTarget', next.AssigneesByTarget);
                onChange(next.Targets);
              }}
              placeholder={t('select')}
              empty={t('noMatchedFound')}
            />
          </FormField>
        );
      }}
    />
  );
};
